import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { upsertLessonProgress } from '../../lib/supabaseProgress';
import { playSuccessSound } from '../../lib/sfx';

const FALLBACK_VIDEO_URL =
  '/media/lesson1-intro.mp4';

const CHECKPOINTS = [20, 80, 98, 124, 149];

function Lesson1InteractiveVideo({
  lessonData,
  language,
  user,
  onClose,
  onComplete
}) {
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [activeCheckpoint, setActiveCheckpoint] = useState(-1);
  const [checkpointDone, setCheckpointDone] = useState(() => Array(CHECKPOINTS.length).fill(false));
  const [videoEnded, setVideoEnded] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizSelectedOptionId, setQuizSelectedOptionId] = useState('');
  const [quizChecked, setQuizChecked] = useState(false);
  const [quizCorrectCount, setQuizCorrectCount] = useState(0);
  const [quizAnsweredIds, setQuizAnsweredIds] = useState({});
  const [quizCompleted, setQuizCompleted] = useState(false);

  const videoUrl = lessonData?.segments?.find((s) => s?.videoUrl)?.videoUrl || FALLBACK_VIDEO_URL;
  const lessonDbId = lessonData?.lessonDbId || null;
  const lessonStepCount = Math.max(1, Number(lessonData?.segments?.length || 1));
  const checkpointCount = CHECKPOINTS.length;
  const totalMilestones = checkpointCount + 2; // intro + checkpoints + final
  const lessonQuiz = lessonData?.quiz || null;
  const quizQuestions = Array.isArray(lessonQuiz?.questions) ? lessonQuiz.questions : [];
  const hasLessonQuiz = Boolean(quizQuestions.length > 0);
  const currentQuizQuestion = hasLessonQuiz ? quizQuestions[Math.min(quizIndex, quizQuestions.length - 1)] : null;
  const quizScorePct = hasLessonQuiz ? Math.round((quizCorrectCount / quizQuestions.length) * 100) : 0;
  const quizPassScore = Number(lessonQuiz?.passScore || 0);
  const quizPassed = quizCompleted ? quizScorePct >= quizPassScore : false;

  const labels = useMemo(
    () =>
      language === 'ro'
        ? {
            hookTitle: 'Există mai multe posibilități pe această tablă decât atomi în univers. Vrei să le vezi?',
            startJourney: 'Începe Călătoria',
            pause1Title: 'Infinitul Tablei',
            pause1Text:
              'Șahul are aproximativ 10 la puterea 120 mutări posibile. Asta înseamnă că există mai multe moduri de a juca o partidă decât atomi în întreg Universul. Ești gata să explorezi infinitul?',
            unlock1: 'Continuă povestea',
            pause2Title: 'Evoluția Reginei',
            pause2Text:
              'Știai că în trecut, Dama era cea mai slabă piesă, mutând doar un pătrățel pe diagonală? Totul s-a schimbat în secolul XV. Dama a primit puteri nelimitate, transformând șahul dintr-un asediu lent într-un război al vitezei. Alege piesa care a rescris regulile.',
            cardA: 'Vizirul (vechea regulă - slab)',
            cardB: 'Regina (noua regulă - puternică)',
            pause3Title: 'Omul vs. Mașina',
            pause3Text:
              'Anul 1997. Momentul în care istoria s-a frânt în două. Garry Kasparov, cel mai mare geniu al epocii, s-a trezit față în față cu un monstru de siliciu: Deep Blue. Nu a fost doar o partidă de șah, a fost bătălia pentru supremația minții umane. Ești gata să vezi cine a clipit primul?',
            pause4Title: 'Regina de Fier',
            pause4Text:
              'Într-o lume care îi spunea că șahul este un joc al bărbaților, o adolescentă de 15 ani a ales să rescrie realitatea. Judit Polgár nu a vrut să fie cea mai bună jucătoare, ci a vrut să-i învingă pe toți. A spart barierele de gen cu o agresivitate sclipitoare, demonstrând că pe tabla de șah, singurul lucru care contează este forța minții tale. Descoperă legenda care a doborât giganții.',
            pause5Title: 'Regele Intuiției',
            pause5Text:
              'După decenii de dominare a computerelor, a apărut el. Magnus Carlsen nu joacă doar șah; el pictează pe tablă folosind presiunea psihologică și o intuiție aproape nepământeană. Într-o eră a algoritmilor reci, el a demonstrat că spiritul uman și rezistența mentală sunt armele supreme. Află cum cel mai relaxat campion din istorie a devenit de neînvins.',
            finalTitle: 'Istoria s-a scris. Acum e rândul tău.',
            finalText:
              'Ai văzut cum șahul a schimbat lumea. La ChessMindset.academy, te învățăm să gândești clar sub presiune.',
            finalCta: 'Vreau să încep să explorez!',
            startQuiz: 'Începe quiz-ul',
            knowledgeQuiz: 'Quiz de consolidare',
            questionLabel: 'Întrebarea',
            nextQuestion: 'Următoarea întrebare',
            finishQuiz: 'Finalizează quiz-ul',
            finishLesson: 'Finalizează lecția',
            quizDone: 'Quiz finalizat',
            passScore: 'Pragul de trecere',
            coachTipRetry: 'Poți finaliza lecția, dar recomandarea de antrenor este să refaci quiz-ul.',
            back: 'Înapoi'
          }
        : {
            hookTitle: 'There are more possibilities on this board than atoms in the universe. Want to see them?',
            startJourney: 'Start the Journey',
            pause1Title: 'Cosmic Hook',
            pause1Text:
              'Chess has approximately 10 to the power of 120 possible moves. That means there are more ways to play one game than atoms in the entire Universe. Ready to explore the infinite?',
            unlock1: 'Continue the story',
            pause2Title: 'The Queen Evolution',
            pause2Text:
              'Did you know the Queen used to be the weakest piece, moving only one square at a time? Everything changed in the 15th century. The Queen was granted unlimited power, turning chess from a slow siege into a war of speed. Choose the card that rewrote the rules.',
            cardA: 'Vizier (old rule - weak)',
            cardB: 'Queen (new rule - strong)',
            pause3Title: 'Man vs. Machine',
            pause3Text:
              "The year is 1997. The moment history split in two. Garry Kasparov, the greatest genius of his time, stood face-to-face with a silicon monster: Deep Blue. This wasn't just a game of chess; it was the ultimate battle for the supremacy of the human mind. Ready to see who blinked first?",
            pause4Title: 'The Iron Queen',
            pause4Text:
              'In a world that told her chess was a man’s game, a 15-year-old girl chose to rewrite reality. Judit Polgár didn’t want to be the best female player; she wanted to defeat them all. She shattered gender barriers with brilliant aggression, proving that on the chessboard, the only thing that matters is the power of your mind. Discover the legend who took down giants.',
            pause5Title: 'The King of Intuition',
            pause5Text:
              'After decades of computer dominance, he arrived. Magnus Carlsen doesn’t just play chess; he paints on the board using psychological pressure and an almost unearthly intuition. In an era of cold algorithms, he proved that the human spirit and mental resilience are the ultimate weapons. Discover how the most relaxed champion in history became unbeatable.',
            finalTitle: 'History is written. Now it is your turn.',
            finalText:
              'You saw how chess changed the world. At ChessMindset.academy we train clear thinking under pressure.',
            finalCta: 'I want to train my Mindset',
            startQuiz: 'Start quiz',
            knowledgeQuiz: 'Knowledge Quiz',
            questionLabel: 'Question',
            nextQuestion: 'Next question',
            finishQuiz: 'Finish quiz',
            finishLesson: 'Finish lesson',
            quizDone: 'Quiz completed',
            passScore: 'Passing score',
            coachTipRetry: 'You can finish the lesson, but coach recommendation is to retry the quiz.',
            back: 'Back'
          },
    [language]
  );

  const completedCount = checkpointDone.filter(Boolean).length;
  const segmentProgress = Math.min(totalMilestones, Math.max(0, 1 + completedCount + (videoEnded ? 1 : 0)));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const forceSubtitleTrack = useCallback(() => {
    const el = videoRef.current;
    if (!el || !el.textTracks) return;
    const wantedLang = language === 'ro' ? 'ro' : 'en';

    try {
      for (let i = 0; i < el.textTracks.length; i += 1) {
        const tt = el.textTracks[i];
        const lang = String(tt.language || '').toLowerCase();
        tt.mode = lang === wantedLang ? 'showing' : 'disabled';
      }
    } catch {
      // ignore platform-specific textTrack quirks
    }
  }, [language]);

  useEffect(() => {
    if (!started) return;
    const t = window.setTimeout(forceSubtitleTrack, 120);
    return () => window.clearTimeout(t);
  }, [started, language, forceSubtitleTrack]);

  const saveCheckpointProgress = async (cpIndex) => {
    if (!lessonDbId || !user?.id) return;
    // Map checkpoint completion proportionally to real DB steps.
    // Keeps UI and DB in sync even when checkpoint count differs from lesson step count.
    const checkpointsDone = Math.max(0, Math.min(checkpointCount, cpIndex + 1));
    const completedEquivalent = Math.floor((checkpointsDone / checkpointCount) * lessonStepCount);
    const nextStep = Math.min(lessonStepCount, Math.max(1, completedEquivalent + 1));
    try {
      await upsertLessonProgress({
        lessonDbId,
        currentStep: nextStep,
        completed: false
      });
    } catch {
      // keep UX smooth even if DB fails
    }
  };

  const handleCheckpointDone = async (cpIndex) => {
    if (checkpointDone[cpIndex]) return;
    const next = [...checkpointDone];
    next[cpIndex] = true;
    setCheckpointDone(next);
    setActiveCheckpoint(-1);
    await saveCheckpointProgress(cpIndex);
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };

  const handleVideoTime = () => {
    if (!started || !videoRef.current) return;
    for (let i = 0; i < CHECKPOINTS.length; i += 1) {
      if (!checkpointDone[i] && videoRef.current.currentTime >= CHECKPOINTS[i]) {
        videoRef.current.pause();
        setActiveCheckpoint(i);
        break;
      }
    }
  };

  const finishLesson = async () => {
    if (lessonDbId && user?.id) {
      try {
        await upsertLessonProgress({
          lessonDbId,
          currentStep: lessonStepCount,
          completed: true
        });
      } catch {
        // continue UX flow even if save fails
      }
    }
    if (typeof onComplete === 'function') {
      await onComplete({
        lessonDbId,
        lessonTitle: lessonData?.title || null
      });
      return;
    }
    onClose();
  };

  const handleSelectQuizAnswer = useCallback((optionId) => {
    if (!currentQuizQuestion || quizChecked) return;
    const picked = String(optionId || '');
    if (!picked) return;

    setQuizSelectedOptionId(picked);
    setQuizChecked(true);
    const qid = String(currentQuizQuestion.id || `q-${quizIndex}`);
    const alreadyAnswered = Boolean(quizAnsweredIds[qid]);
    if (!alreadyAnswered && picked === String(currentQuizQuestion.correctOptionId || '')) {
      setQuizCorrectCount((prev) => prev + 1);
    }
    if (!alreadyAnswered) {
      setQuizAnsweredIds((prev) => ({ ...prev, [qid]: true }));
    }
  }, [currentQuizQuestion, quizChecked, quizIndex, quizAnsweredIds]);

  const handleNextQuizQuestion = useCallback(() => {
    if (!hasLessonQuiz) return;
    if (quizIndex >= quizQuestions.length - 1) {
      setQuizCompleted(true);
      return;
    }
    setQuizIndex((prev) => prev + 1);
    setQuizSelectedOptionId('');
    setQuizChecked(false);
  }, [hasLessonQuiz, quizIndex, quizQuestions.length]);

  const subtitleSrc = language === 'ro' ? '/media/lesson1-intro.ro.vtt' : '/media/lesson1-intro.en.vtt';
  const subtitleLang = language === 'ro' ? 'ro' : 'en';
  const subtitleLabel = language === 'ro' ? 'Română' : 'English';

  const renderCheckpoint = () => {
    if (activeCheckpoint < 0) return null;

    if (activeCheckpoint === 0) {
      return (
        <div className="l1v-card">
          <h3 className="l1v-hook-title l1v-hook-cosmic">
            <span aria-hidden="true">✨</span>
            {labels.pause1Title}
          </h3>
          <p>{labels.pause1Text}</p>
          <button
            className="btn-nav btn-next"
            onClick={async () => {
              playSuccessSound();
              await handleCheckpointDone(0);
            }}
          >
            {labels.unlock1}
          </button>
        </div>
      );
    }

    if (activeCheckpoint === 1) {
      return (
        <div className="l1v-card">
          <h3 className="l1v-hook-title l1v-hook-queen">
            <span aria-hidden="true">👑</span>
            {labels.pause2Title}
          </h3>
          <p>{labels.pause2Text}</p>
          <button
            className="btn-nav btn-next"
            onClick={async () => {
              playSuccessSound();
              await handleCheckpointDone(1);
            }}
          >
            {labels.unlock1}
          </button>
        </div>
      );
    }

    if (activeCheckpoint === 2) {
      return (
        <div className="l1v-card">
          <h3 className="l1v-hook-title l1v-hook-battle">
            <span aria-hidden="true">⚔️</span>
            {labels.pause3Title}
          </h3>
          <p>{labels.pause3Text}</p>
          <button
            className="btn-nav btn-next"
            onClick={async () => {
              playSuccessSound();
              await handleCheckpointDone(2);
            }}
          >
            {labels.unlock1}
          </button>
        </div>
      );
    }

    if (activeCheckpoint === 3) {
      return (
        <div className="l1v-card">
          <h3 className="l1v-hook-title l1v-hook-iron">
            <span aria-hidden="true">⚡</span>
            {labels.pause4Title}
          </h3>
          <p>{labels.pause4Text}</p>
          <button
            className="btn-nav btn-next"
            onClick={async () => {
              playSuccessSound();
              await handleCheckpointDone(3);
            }}
          >
            {labels.unlock1}
          </button>
        </div>
      );
    }

    if (activeCheckpoint === 4) {
      return (
        <div className="l1v-card">
          <h3 className="l1v-hook-title l1v-hook-intuition">
            <span aria-hidden="true">🧠</span>
            {labels.pause5Title}
          </h3>
          <p>{labels.pause5Text}</p>
          <button
            className="btn-nav btn-next"
            onClick={async () => {
              playSuccessSound();
              await handleCheckpointDone(4);
            }}
          >
            {labels.unlock1}
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="lesson-viewer">
      <div className="lesson-header">
        <button className="btn-close" onClick={onClose}>← {labels.back}</button>
        <h2 className="lesson-title">{lessonData?.title || 'Lesson 1'}</h2>
        <div className="lesson-header-spacer" aria-hidden="true" />
      </div>

      <div
        className="l1v-segments"
        style={{ gridTemplateColumns: `repeat(${totalMilestones}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: totalMilestones }).map((_, i) => (
          <span key={i} className={`l1v-seg ${i < segmentProgress ? 'done' : ''}`} />
        ))}
      </div>

      <div className="l1v-wrap">
        {!started ? (
          <div className="l1v-cover">
            <video src={videoUrl} muted playsInline className="l1v-cover-video" />
            <div className="l1v-cover-overlay">
              <h3>{labels.hookTitle}</h3>
              <button
                className="btn-nav btn-next"
                onClick={() => {
                  setStarted(true);
                  setTimeout(() => {
                    if (videoRef.current) videoRef.current.play().catch(() => {});
                  }, 80);
                }}
              >
                {labels.startJourney}
              </button>
            </div>
          </div>
        ) : (
          <div className="l1v-player">
            <video
              ref={videoRef}
              src={videoUrl}
              controls
              playsInline
              onLoadedMetadata={forceSubtitleTrack}
              onTimeUpdate={handleVideoTime}
              onEnded={() => setVideoEnded(true)}
            >
              <track
                key={subtitleLang}
                kind="subtitles"
                srcLang={subtitleLang}
                label={subtitleLabel}
                src={subtitleSrc}
                default
              />
            </video>
            {activeCheckpoint >= 0 && <div className="l1v-overlay">{renderCheckpoint()}</div>}
          </div>
        )}
      </div>

      {videoEnded && completedCount === checkpointCount && (
        <div className="lesson-text-container">
          <h3 style={{ marginBottom: 8 }}>{labels.finalTitle}</h3>
          <p style={{ marginBottom: 16 }}>{labels.finalText}</p>
          {!hasLessonQuiz && (
            <button className="btn-nav btn-next" onClick={finishLesson}>
              {labels.finalCta}
            </button>
          )}
          {hasLessonQuiz && !quizStarted && (
            <button
              className="btn-nav btn-next"
              onClick={() => {
                setQuizStarted(true);
                setQuizIndex(0);
                setQuizSelectedOptionId('');
                setQuizChecked(false);
              }}
            >
              {labels.finalCta}
            </button>
          )}

          {hasLessonQuiz && quizStarted && (
            <div className="lesson-task" style={{ marginTop: 14 }}>
              <div className="lesson-task-title">
                {labels.knowledgeQuiz}
              </div>
              {!quizCompleted && currentQuizQuestion && (
                <>
                  <div className="lesson-task-text" style={{ marginBottom: 10 }}>
                    {labels.questionLabel} {Math.min(quizIndex + 1, quizQuestions.length)} / {quizQuestions.length}
                  </div>
                  <div className="lesson-task-text" style={{ marginBottom: 10 }}>
                    {currentQuizQuestion.question}
                  </div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {(currentQuizQuestion.options || []).map((opt) => {
                      const oid = String(opt?.id || '');
                      const selected = quizSelectedOptionId === oid;
                      const isCorrect = oid === String(currentQuizQuestion.correctOptionId || '');
                      const isWrongSelected = quizChecked && selected && !isCorrect;
                      const isCorrectSelected = quizChecked && selected && isCorrect;
                      return (
                        <button
                          key={oid}
                          className="btn-card"
                          onClick={() => handleSelectQuizAnswer(oid)}
                          style={{
                            textAlign: 'left',
                            borderColor: isCorrectSelected
                              ? '#16a34a'
                              : isWrongSelected
                                ? '#dc2626'
                                : selected
                                  ? '#2563eb'
                                  : '#cbd5e1',
                            background: isCorrectSelected
                              ? '#ecfdf5'
                              : isWrongSelected
                                ? '#fef2f2'
                                : selected
                                  ? '#eff6ff'
                                  : '#ffffff'
                          }}
                        >
                          <strong style={{ marginRight: 8 }}>{oid.toUpperCase()}.</strong>
                          {opt?.text}
                        </button>
                      );
                    })}
                  </div>
                  <div className="lesson-action-ui" style={{ marginTop: 12 }}>
                    {quizChecked && (
                      <button
                        className="btn-card lesson-action-primary"
                        onClick={handleNextQuizQuestion}
                      >
                        {quizIndex < quizQuestions.length - 1
                          ? labels.nextQuestion
                          : labels.finishQuiz}
                      </button>
                    )}
                  </div>
                  {quizChecked && currentQuizQuestion.explanation && (
                    <div
                      className="lesson-feedback"
                      style={{
                        marginTop: 10,
                        borderColor:
                          quizSelectedOptionId === String(currentQuizQuestion.correctOptionId || '')
                            ? '#16a34a'
                            : '#dc2626',
                        background:
                          quizSelectedOptionId === String(currentQuizQuestion.correctOptionId || '')
                            ? '#ecfdf5'
                            : '#fef2f2',
                        color:
                          quizSelectedOptionId === String(currentQuizQuestion.correctOptionId || '')
                            ? '#14532d'
                            : '#7f1d1d'
                      }}
                    >
                      {currentQuizQuestion.explanation}
                    </div>
                  )}
                </>
              )}

              {quizCompleted && (
                <>
                  <div
                    className="lesson-feedback"
                    style={{
                      borderColor: quizPassed ? '#16a34a' : '#f59e0b',
                      background: quizPassed ? '#ecfdf5' : '#fffbeb',
                      color: quizPassed ? '#14532d' : '#92400e'
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>
                      {labels.quizDone}: {quizCorrectCount}/{quizQuestions.length} ({quizScorePct}%)
                    </div>
                    <div style={{ marginTop: 6 }}>
                      {labels.passScore}: {quizPassScore}%.
                    </div>
                    {!quizPassed && (
                      <div style={{ marginTop: 8 }}>
                        {labels.coachTipRetry}
                      </div>
                    )}
                  </div>
                  <button className="btn-nav btn-next" onClick={finishLesson} style={{ marginTop: 12 }}>
                    {labels.finishLesson}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
}

export default Lesson1InteractiveVideo;
